import React, { useEffect, useState } from "react";
import { Grid, Typography, Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  getSettings,
  getSettingsError,
  getSettingsStatus,
  selectAllSettings,
} from "../../../features/settingSlice";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import Loading from "../../../components/loading";
import Error from "../../../components/Error";
import EditIcon from "@mui/icons-material/Edit";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import TuneIcon from "@mui/icons-material/Tune";
import SlideshowIcon from "@mui/icons-material/Slideshow";
import PaymentsIcon from "@mui/icons-material/Payments";
import TakeoutDiningIcon from "@mui/icons-material/TakeoutDining";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import AppBarSearch from "../../../global/appBarSearch";
import { useRestaurant } from "../../../hooks/RestaurantContext";
import { ResponsiveProvider } from "../../../hooks/uiHook";
import CopyLink from "../../../components/copyLink";
import { useTranslation } from "react-i18next";
import {
  SectionCard,
  SettingRow,
  SettingValue,
  StatusChip,
  useSettingsCardSx,
} from "../../../components/settingsLayout";

const apiUrl = process.env.REACT_APP_MEDIA_URL;

const bannerImageSrc = (banner) => {
  if (!banner) return "";
  const value = String(banner).replace(/\\/g, "/");
  if (/^https?:\/\//i.test(value)) return value;
  return `${apiUrl}/${value}`;
};

const isEnabled = (item) => item?.isActive !== false;

const SettingsManagement = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { restaurantId } = useRestaurant();
  const settings = useSelector(selectAllSettings);
  const error = useSelector(getSettingsError);
  const settingStatus = useSelector(getSettingsStatus);
  const [copied, setCopied] = useState(false);
  const cardSx = useSettingsCardSx();

  const handleSubmit = (event) => {
    event.preventDefault();
    navigate("modify", {
      state: {
        qrCode: settings.qrCode,
        tva: settings.tva,
        maxDrink: settings.maxDrink,
        maxDessert: settings.maxDessert,
        maxExtras: settings.maxExtras,
        banner: settings.banner,
        method: settings.method,
        pack: settings.pack,
        carouselDuration: settings.carouselDuration,
        carouselTiming: settings.carouselTiming,
        defaultLanguage: settings.defaultLanguage || "fr",
        loyaltyEnabled: settings.loyaltyEnabled ?? false,
        loyaltyEarnPoints: settings.loyaltyEarnPoints ?? 1,
        loyaltyRedeemPoints: settings.loyaltyRedeemPoints ?? 100,
        loyaltyMaxPercent: settings.loyaltyMaxPercent ?? 50,
        cardMinAmount: settings.cardMinAmount ?? 10,
        cardMinMessage: settings.cardMinMessage,
      },
    });
  };

  useEffect(() => {
    dispatch(getSettings(restaurantId));
  }, [dispatch, restaurantId]);

  const bannerSrc = bannerImageSrc(settings?.banner);

  return (
    <div className="main-application">
      <ResponsiveProvider maxWidth={900}>
        <AppBarSearch
          handleSubmit={handleSubmit}
          title={t("pages.settingsManagement")}
          buttonTitle={t("actions.editSettings")}
          buttonColor={"info"}
          buttonIcon={<EditIcon />}
        />
      </ResponsiveProvider>
      <main>
        <Box sx={{ my: 2 }}>
          {settingStatus === "loading" ? (
            <Loading />
          ) : settingStatus === "fetchError" ? (
            <Error>{error}</Error>
          ) : (
            settings && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <SectionCard
                    sx={cardSx}
                    icon={<TuneIcon color="primary" />}
                    title={t("settings.sectionGeneral")}
                  >
                    <SettingRow label={t("fields.qrLink")} stacked>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          px: 1.25,
                          py: 0.75,
                          width: "100%",
                          borderRadius: 1.5,
                          border: "1px solid",
                          borderColor: "divider",
                          bgcolor: "action.hover",
                        }}
                      >
                        <QrCode2Icon fontSize="small" color="action" />
                        <CopyLink
                          copied={copied}
                          setCopied={setCopied}
                          url={settings?.qrCode}
                        />
                      </Box>
                    </SettingRow>
                    <SettingRow label={t("fields.tva")}>
                      <SettingValue>{`${settings?.tva || 0}%`}</SettingValue>
                    </SettingRow>
                    <SettingRow label={t("fields.defaultLanguage")}>
                      <SettingValue>
                        {t(`language.${settings?.defaultLanguage || "fr"}`)}
                      </SettingValue>
                    </SettingRow>
                    <SettingRow label={t("fields.loyaltyEnabled")}>
                      <SettingValue>
                        {(settings?.loyaltyEnabled ?? false) ? t("common.yes") : t("common.no")}
                      </SettingValue>
                    </SettingRow>
                    <SettingRow label={t("fields.loyaltyEarnPoints")}>
                      <SettingValue>
                        {`${settings?.loyaltyEarnPoints ?? 1} / 1 ${settings?.defaultCurrency || ""}`.trim()}
                      </SettingValue>
                    </SettingRow>
                    <SettingRow label={t("fields.loyaltyRedeemPoints")}>
                      <SettingValue>
                        {`${settings?.loyaltyRedeemPoints ?? 100} = 1 ${settings?.defaultCurrency || ""}`.trim()}
                      </SettingValue>
                    </SettingRow>
                    <SettingRow label={t("fields.loyaltyMaxPercent")} last>
                      <SettingValue>{`${settings?.loyaltyMaxPercent ?? 50}%`}</SettingValue>
                    </SettingRow>
                  </SectionCard>
                </Grid>

                <Grid item xs={12} md={6}>
                  <SectionCard
                    sx={cardSx}
                    icon={<SlideshowIcon color="primary" />}
                    title={t("settings.sectionDisplay")}
                  >
                    <SettingRow label={t("fields.carouselDurationShort")}>
                      <SettingValue>
                        {`${settings?.carouselDuration || 5}s`}
                      </SettingValue>
                    </SettingRow>
                    <SettingRow label={t("fields.carouselIdleShort")} last>
                      <SettingValue>
                        {`${settings?.carouselTiming || 120}s`}
                      </SettingValue>
                    </SettingRow>
                  </SectionCard>
                </Grid>

                <Grid item xs={12} md={6}>
                  <SectionCard
                    sx={cardSx}
                    icon={<PaymentsIcon color="primary" />}
                    title={t("settings.sectionPayments")}
                  >
                    <OptionRow
                      label={t("fields.cardPayment")}
                      value={
                        settings?.method?.[0]?.label || t("fields.cardDefault")
                      }
                      active={isEnabled(settings?.method?.[0])}
                    />
                    <SettingRow label={t("fields.cardMinAmount")}>
                      <SettingValue>
                        {`${settings?.cardMinAmount ?? 10} ${settings?.defaultCurrency || "€"}`}
                      </SettingValue>
                    </SettingRow>
                    <SettingRow label={t("fields.cardMinMessage")}>
                      <SettingValue>
                        {settings?.cardMinMessage?.[settings?.defaultLanguage || "fr"] ||
                          settings?.cardMinMessage?.fr}
                      </SettingValue>
                    </SettingRow>
                    <OptionRow
                      label={t("fields.cashPayment")}
                      value={
                        settings?.method?.[1]?.label || t("fields.cashDefault")
                      }
                      active={isEnabled(settings?.method?.[1])}
                      last
                    />
                  </SectionCard>
                </Grid>

                <Grid item xs={12} md={6}>
                  <SectionCard
                    sx={cardSx}
                    icon={<TakeoutDiningIcon color="primary" />}
                    title={t("settings.sectionFulfillment")}
                  >
                    <OptionRow
                      label={t("fields.dineInModeShort")}
                      value={
                        settings?.pack?.[0]?.label || t("fields.dineInDefault")
                      }
                      active={isEnabled(settings?.pack?.[0])}
                    />
                    <OptionRow
                      label={t("fields.takeawayModeShort")}
                      value={
                        settings?.pack?.[1]?.label ||
                        t("fields.takeawayDefault")
                      }
                      active={isEnabled(settings?.pack?.[1])}
                      last
                    />
                  </SectionCard>
                </Grid>

                <Grid
                  item
                  xs={12}
                  sx={{ display: "flex", justifyContent: "center" }}
                >
                  <SectionCard
                    sx={{
                      ...cardSx,
                      height: "auto",
                      width: "fit-content",
                      maxWidth: "100%",
                    }}
                    icon={<ImageOutlinedIcon color="primary" />}
                    title={t("fields.banner")}
                  >
                    {bannerSrc ? (
                      <Box
                        component="img"
                        src={bannerSrc}
                        alt={t("fields.banner")}
                        loading="lazy"
                        sx={{
                          display: "block",
                          width: "auto",
                          height: "auto",
                          maxWidth: 720,
                          maxHeight: 220,
                          objectFit: "contain",
                          borderRadius: 1.5,
                          border: "1px solid",
                          borderColor: "divider",
                          bgcolor: "action.hover",
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1,
                          minWidth: 280,
                          minHeight: 160,
                          borderRadius: 1.5,
                          border: "1px dashed",
                          borderColor: "divider",
                          bgcolor: "action.hover",
                          color: "text.secondary",
                        }}
                      >
                        <ImageOutlinedIcon sx={{ fontSize: 36, opacity: 0.7 }} />
                        <Typography variant="body2">
                          {t("fields.noBanner")}
                        </Typography>
                      </Box>
                    )}
                  </SectionCard>
                </Grid>
              </Grid>
            )
          )}
        </Box>
      </main>
    </div>
  );
};

const OptionRow = ({ label, value, active, last }) => (
  <SettingRow label={label} last={last}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <SettingValue>{value}</SettingValue>
      <StatusChip active={active} />
    </Box>
  </SettingRow>
);

export default SettingsManagement;
